import { useEffect, useState } from "react";
import type { ConversationSummary } from "./types";

/** Minutes after which a waiting customer is flagged amber, then red. */
export const WAIT_WARN_MIN = 5;
export const WAIT_LATE_MIN = 15;

type WaitFields = Pick<ConversationSummary, "status" | "lastInboundAt" | "lastMessageAt">;

/**
 * A person has to answer: the conversation is with the team (not the IA, not closed)
 * and the last message came from the customer. Inbound messages set lastInboundAt and
 * lastMessageAt to the same instant; any reply moves lastMessageAt past it.
 */
export function isWaiting(c: WaitFields): boolean {
  if (c.status !== "human" || !c.lastInboundAt) return false;
  return !c.lastMessageAt || Date.parse(c.lastMessageAt) <= Date.parse(c.lastInboundAt);
}

export function waitMinutes(lastInboundAt: string | null, now: number): number {
  return lastInboundAt ? Math.max(0, Math.floor((now - Date.parse(lastInboundAt)) / 60_000)) : 0;
}

export function waitLevel(minutes: number): "" | "warn" | "late" {
  if (minutes >= WAIT_LATE_MIN) return "late";
  if (minutes >= WAIT_WARN_MIN) return "warn";
  return "";
}

/** "4 min", "2 h", "3 d". */
export function waitLabel(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  if (minutes < 24 * 60) return `${Math.floor(minutes / 60)} h`;
  return `${Math.floor(minutes / (24 * 60))} d`;
}

/** Current time that re-renders every `ms`, so waiting times keep counting up. */
export function useNow(ms = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(timer);
  }, [ms]);
  return now;
}
